import React, { useEffect, useState, useCallback } from "react";
import { showError } from "../common/errors";
import { useDispatch, useSelector } from "react-redux";
import { pick, isEmpty } from "lodash";
import { App, Form, Input, Radio, Select, Checkbox, Button, Spin } from "antd";
import {
  LeftOutlined,
  SaveOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import dayjs from "dayjs";

import { apiBase } from "../utils/apiBase";
import UserSelect from "../views/common/UserSelect";
import {
  activityTypeOptions,
  activityPriorityOptions,
} from "../common/activities";
import { fetchUndoneActivityCount } from "../actions/activities";
import DateTimePicker from "../views/common/DateTimePicker";

import styles from "./ActivityWidgetView.module.scss";

const formFields = [
  "title",
  "description",
  "type",
  "priority",
  "due_at",
  "user_id",
  "done",
];

const buildFormValues = (data) => ({
  ...pick(data, formFields),
  due_at: data.due_at ? dayjs(data.due_at) : null,
});

const ActivityWidgetView = ({ id, onBack }) => {
  const { modal } = App.useApp();
  const { message } = useSelector((state) => state.antd);
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [activity, setActivity] = useState({});

  const loadActivity = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiBase.get(`/activities/${id}`);
      const data = res.data || {};
      setActivity(data);
      form.setFieldsValue(buildFormValues(data));
    } catch (err) {
      showError(err, `Failed to load activity ${id}`);
    } finally {
      setLoading(false);
    }
  }, [id, form]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const payload = {
        ...values,
        description: values.description ?? null,
        due_at: values.due_at ? values.due_at.toISOString() : null,
        done: !!values.done,
      };
      const res = await apiBase.patch(`/activities/${id}`, payload);
      setActivity(res.data);
      dispatch(fetchUndoneActivityCount());
      message.success("Activity updated");
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        JSON.stringify(err.response?.data?.errors) ||
        "Failed to update activity";
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const deleteActivity = async () => {
    try {
      setLoading(true);
      await apiBase.delete(`/activities/${id}`);
      dispatch(fetchUndoneActivityCount());

      message.success("Activity deleted");
      onBack?.();
    } catch (err) {
      showError(err, "Failed to delete activity");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    modal.confirm({
      title: "Are you sure you want to delete this activity?",
      icon: <ExclamationCircleFilled />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      okButtonProps: { disabled: loading, loading },
      cancelButtonProps: { disabled: loading, loading },
      onOk: deleteActivity,
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Button
          icon={<LeftOutlined />}
          size="small"
          onClick={onBack}
          disabled={loading}
        >
          Back
        </Button>
        <div className={styles.actions}>
          <Button
            icon={<DeleteOutlined />}
            size="small"
            loading={loading}
            onClick={handleDelete}
          >
            Delete
          </Button>
          <Button
            icon={<SaveOutlined />}
            size="small"
            loading={loading}
            onClick={() => form.submit()}
          >
            Save
          </Button>
        </div>
      </div>

      <div className={styles.form}>
        {loading && isEmpty(activity) ? (
          <div className={styles.loading}>
            <Spin />
          </div>
        ) : (
          !isEmpty(activity) && (
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              disabled={loading}
            >
              <Form.Item
                label="Title"
                name="title"
                rules={[{ required: true, message: "Title is required" }]}
              >
                <Input placeholder="Title" />
              </Form.Item>

              <Form.Item
                className={styles.descriptionItem}
                label="Description"
                name="description"
              >
                <Input.TextArea placeholder="Description" />
              </Form.Item>

              <Form.Item
                label="Type"
                name="type"
                rules={[{ required: true, message: "Type is required" }]}
              >
                <Radio.Group
                  options={activityTypeOptions}
                  optionType="button"
                  className={styles.typeGroup}
                />
              </Form.Item>

              <Form.Item label="Priority" name="priority">
                <Select
                  options={activityPriorityOptions}
                  allowClear
                  placeholder="Priority"
                />
              </Form.Item>

              <Form.Item
                label="Due at"
                name="due_at"
                rules={[{ required: true, message: "Due date is required" }]}
              >
                <DateTimePicker />
              </Form.Item>

              <Form.Item
                label="User"
                name="user_id"
                rules={[{ required: true, message: "User is required" }]}
              >
                <UserSelect />
              </Form.Item>

              <Form.Item label="Done" name="done" valuePropName="checked">
                <Checkbox />
              </Form.Item>
            </Form>
          )
        )}
      </div>
    </div>
  );
};

export default ActivityWidgetView;
